import {
  inferColorScheme,
  resolveStorefrontThemeVars,
  type StorefrontTheme,
} from "@loot-os/api-contract";
import { loadStorefrontFonts } from "@loot-os/ui/lib/storefrontFonts";
import { cn } from "@loot-os/ui/lib/utils";
import { type CSSProperties, type ReactNode, useEffect } from "react";

/**
 * Renders its children in a tenant's storefront palette without recoloring the
 * dashboard around it.
 *
 * The storefront applies the same variables to `<html>`; here they go on one
 * container instead. That works because @loot-os/ui's `@theme inline` keeps the
 * *reference* live rather than baking the value — the built CSS really does emit
 * `.bg-card{background-color:var(--card)}` and
 * `.rounded-xl{border-radius:calc(var(--radius) * 1.4)}` — so any utility in the
 * subtree resolves through whatever these variables say here.
 *
 * `--color-border` is the exception, and re-declaring it is load-bearing.
 * Tailwind's base layer sets `*{border-color:var(--color-border)}` while
 * `--color-border:var(--border)` is declared at `:root`. Custom properties
 * substitute at the *declaring* element, so that resolves against the
 * dashboard's palette and inherits as a finished color — a scoped `--border`
 * alone would leave every bare `border` (Card, Table, CardFooter) dashboard-
 * purple on a tenant-colored surface. Re-declaring it here moves the
 * substitution into this subtree.
 *
 * `color-scheme` follows the background's lightness, exactly as the storefront's
 * `applyPageTheme` sets it on `<html>`, so a light palette previews with light
 * native controls here just as shoppers will see them. The scheme also picks a
 * scope class: `light-scope` turns the `dark:` variant off inside a light palette
 * while the dashboard is dark, and `dark-scope` turns it on inside a dark palette
 * while the dashboard is light.
 *
 * Fonts follow the same path: the body stack as `font-family`, the heading stack as
 * `--ds-font-heading` (which h1–h3 read, globals.css), and the catalog fonts they name loaded
 * into the dashboard, so the preview shows the typefaces shoppers get.
 */
export function StorefrontThemeScope({
  theme,
  className,
  children,
}: {
  theme?: StorefrontTheme;
  className?: string;
  children: ReactNode;
}) {
  const { colors, radius, fontFamily, headingFontFamily, fonts } =
    resolveStorefrontThemeVars(theme);
  // At most two (body and heading), as primitives so the effect reruns only when one changes.
  const [firstFont, secondFont] = fonts;
  useEffect(() => {
    void loadStorefrontFonts([firstFont, secondFont].filter((id) => id !== undefined));
  }, [firstFont, secondFont]);

  const colorScheme = inferColorScheme(colors["--background"]);
  const style = {
    ...colors,
    ...(radius ? { "--radius": radius } : {}),
    ...(fontFamily ? { fontFamily } : {}),
    ...(headingFontFamily ? { "--ds-font-heading": headingFontFamily } : {}),
    ...(colorScheme ? { colorScheme } : {}),
    "--color-border": "var(--border)",
  } as CSSProperties;

  // bg-background/text-foreground are explicit: without them the container
  // inherits the dashboard's body colors and only nested surfaces get themed. The scope class
  // makes the `dark:` variants follow the palette's own scheme.
  return (
    <div
      style={style}
      className={cn(
        "bg-background text-foreground",
        colorScheme === "light" && "light-scope",
        colorScheme === "dark" && "dark-scope",
        className,
      )}
    >
      {children}
    </div>
  );
}
